import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ProfileCompletionModal from '../profile/ProfileCompletionModal';
import ClientProfile from '../profile/ClientProfile';
import TrainingMetricsDashboard from './TrainingMetricsDashboard';
import ClientMySessionsTab from '../client/ClientMySessionsTab';
import ClientWorkoutExecution from '../client/ClientWorkoutExecution';
import WorkoutDetailsModal from '../workout/WorkoutDetailsModal';
import MyFlexibleRoutines from '../client/MyFlexibleRoutines';
import SessionBooking from '../booking/SessionBooking';
import ClientCycleReport from '../cycles/ClientCycleReport';
import ClientEoaEBanner from '../eoae/ClientEoaEBanner';
import { MuscleHeatmapCard } from '../workout/MuscleHeatmapCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// CLIENT DASHBOARD (UNIFIED)
// Navigation: Mis Sesiones | Métricas | Perfil
// + Solicitar sesión CTA
// ============================================

const ClientDashboard = ({ user, onLogout, language, t }) => {
  const [activeTab, setActiveTab] = useState('sessions');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const [progressData, setProgressData] = useState(null);
  
  // Active workout/results state
  const [activeWorkout, setActiveWorkout] = useState(null); // { booking, workout }
  const [viewingResults, setViewingResults] = useState(null); // { booking, workout }
  const [showBooking, setShowBooking] = useState(false);
  const [flexRefreshKey, setFlexRefreshKey] = useState(0); // bumps to refetch Mis Rutinas after a workout closes

  const fetchData = async () => {
    try {
      // Only fetch what the client home actually renders. (The heavy
      // /exercises/list ~500KB catalog and /external-workouts were fetched
      // here but never used — they delayed the "training available" card.)
      const results = await Promise.allSettled([
        axios.get(`${API}/timeslots/available`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/workouts?summary=true`),
        axios.get(`${API}/profile`)
      ]);
      
      const [slotsRes, bookingsRes, workoutsRes, profileRes] = results;
      
      if (slotsRes.status === 'fulfilled') setAvailableSlots(slotsRes.value.data);
      if (bookingsRes.status === 'fulfilled') {
        // Handle both paginated response and direct array response
        const bookingsData = bookingsRes.value.data;
        if (Array.isArray(bookingsData)) {
          setMyBookings(bookingsData);
        } else if (bookingsData && Array.isArray(bookingsData.bookings)) {
          setMyBookings(bookingsData.bookings);
        } else {
          setMyBookings([]);
        }
      }
      if (workoutsRes.status === 'fulfilled') setMyWorkouts(workoutsRes.value.data);
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data);

      // Fetch progress insights for the sessions tab
      try {
        const progRes = await axios.get(`${API}/training/client-progress/${user.id}`);
        setProgressData(progRes.data);
      } catch (e) {
        console.error('Progress data unavailable:', e);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const checkProfileCompletion = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
      
      if (!response.data.profile_completed && user.role === 'client') {
        setShowProfileCompletion(true);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  useEffect(() => {
    fetchData();
    checkProfileCompletion();
  }, []);

  const handleProfileComplete = () => {
    setShowProfileCompletion(false);
    checkProfileCompletion();
  };

  const bookSession = async (timeSlotId, notes = '') => {
    try {
      await axios.post(`${API}/bookings`, {
        time_slot_id: timeSlotId,
        client_notes: notes
      });
      fetchData();
      setShowBooking(false);
      alert('✅ ¡Solicitud de reserva enviada con éxito!');
    } catch (error) {
      console.error('Error booking session:', error);
      alert('❌ Error al reservar sesión: ' + (error.response?.data?.detail || 'Error desconocido'));
    }
  };

  // Summary-mode list items omit the heavy `exercises` array. Fetch the full
  // workout on demand (start / view results) so the feed stays light.
  const ensureFullWorkout = async (w) => {
    if (!w || Array.isArray(w.exercises)) return w;
    try {
      const res = await axios.get(`${API}/workouts/${w.id}`);
      return res.data || w;
    } catch (error) {
      console.error('Error loading workout detail:', error);
      return w;
    }
  };

  // Handle starting a workout
  const handleStartWorkout = async (booking, workout) => {
    if (!workout) {
      // Create workout if it doesn't exist
      try {
        const response = await axios.post(`${API}/bookings/${booking.id}/create-workout`);
        const newWorkout = response.data;
        setActiveWorkout({ booking, workout: newWorkout });
      } catch (error) {
        console.error('Error creating workout:', error);
        alert('Error al crear el entrenamiento: ' + (error.response?.data?.detail || 'Error desconocido'));
      }
    } else if (workout.is_finished) {
      // Cannot start a finished workout - show results instead
      alert('Este entrenamiento ya ha sido completado. Mostrando resultados.');
      setViewingResults({ booking, workout: await ensureFullWorkout(workout) });
    } else {
      // Open existing workout (lazy-load full exercises if this came from the summary feed)
      setActiveWorkout({ booking, workout: await ensureFullWorkout(workout) });
    }
  };

  // Handle viewing workout results
  const handleViewResults = async (booking, workout) => {
    setViewingResults({ booking, workout: await ensureFullWorkout(workout) });
  };

  // Handle workout finish
  const handleWorkoutFinish = () => {
    setActiveWorkout(null);
    fetchData();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      {/* Navigation */}
      <nav className="glass-nav p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-3">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_workout-central-9/artifacts/1boxowhf_logo%20btc.PNG" 
              alt="Bétera Training Center" 
              className="h-9 w-9 sm:h-10 sm:w-10 object-contain flex-shrink-0"
            />
            <h1 className="text-base sm:text-2xl font-bold text-white truncate">Betera Training Center</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <span className="hidden md:inline text-blue-200">Bienvenido/a, {user.full_name}</span>
            <span className="badge bg-green-500/20 text-green-200 hidden sm:inline">Cliente</span>
            <button onClick={onLogout} className="btn-secondary text-sm whitespace-nowrap">
              Cerrar sesión
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6">
        {/* End of an Era — shared keepsake banner */}
        <ClientEoaEBanner />
        {/* Shared cycle report banner (Phase 4) */}
        <ClientCycleReport />

        <div className="glass-card p-6 mb-6">
          {/* Tab Navigation - SIMPLIFIED: Sessions | Mis Rutinas | Metrics | Profile + CTA */}
          <div className="flex flex-wrap gap-2 border-b border-blue-400/20 pb-4 mb-6">
            {[
              { key: 'sessions', label: '📅 Mis Sesiones' },
              { key: 'routines', label: '🏠 Mis Rutinas' },
              { key: 'metrics', label: '📊 Métricas' },
              { key: 'profile', label: '👤 Perfil' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === tab.key 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg scale-105' 
                    : 'text-blue-300 hover:text-blue-200 hover:bg-blue-800/20'
                }`}
                data-testid={`client-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
            
            {/* Book Session Button - Only show if booking_mode is booking_flex */}
            {currentUser?.booking_mode === 'booking_flex' && (
              <button
                onClick={() => setShowBooking(true)}
                className="ml-auto btn-primary flex items-center gap-2"
                data-testid="book-session-btn"
              >
                + Reservar sesión
              </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {/* 📅 MY SESSIONS TAB (UNIFIED HUB) */}
            {activeTab === 'sessions' && (
              <ClientMySessionsTab
                bookings={myBookings}
                workouts={myWorkouts}
                onStartWorkout={handleStartWorkout}
                onViewResults={handleViewResults}
                onRefresh={fetchData}
                userId={user.id}
                isBookingFlex={user.booking_mode === 'booking_flex' || !user.booking_mode}
                progressData={progressData}
              />
            )}
            
            {/* 🏠 MIS RUTINAS TAB (Flexible workouts / Rutinas libres) */}
            {activeTab === 'routines' && (
              <MyFlexibleRoutines
                userId={user.id}
                refreshKey={flexRefreshKey}
                onStartWorkout={handleStartWorkout}
                onViewResults={handleViewResults}
              />
            )}
            
            {/* 📊 METRICS TAB */}
            {activeTab === 'metrics' && (
              <>
                <MuscleHeatmapCard clientId={user.id} language={language} className="mb-6" />
                <TrainingMetricsDashboard 
                  userId={user.id}
                  language={language}
                  t={t}
                />
              </>
            )}
            
            {/* 👤 PROFILE TAB */}
            {activeTab === 'profile' && (
              <ClientProfile 
                profile={profile}
                onUpdateProfile={fetchData}
                t={t}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Profile Completion Modal */}
      {showProfileCompletion && (
        <ProfileCompletionModal
          user={currentUser}
          onComplete={handleProfileComplete}
          t={t}
        />
      )}
      
      {/* Book Session Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowBooking(false)}>
          <div className="glass-card max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">➕ Solicitar sesión de entrenamiento</h3>
              <button onClick={() => setShowBooking(false)} className="text-red-400 hover:text-red-300 text-xl">×</button>
            </div>
            <SessionBooking 
              availableSlots={availableSlots}
              onBookSession={bookSession}
              t={t}
            />
          </div>
        </div>
      )}
      
      {/* Active Workout (Full Screen) */}
      {activeWorkout && (
        <ClientWorkoutExecution
          booking={activeWorkout.booking}
          workout={activeWorkout.workout}
          onFinish={handleWorkoutFinish}
          onClose={() => { setActiveWorkout(null); fetchData(); setFlexRefreshKey((k) => k + 1); }}
          t={t}
        />
      )}
      
      {/* Workout Results (canonical Workout Details format, Spanish for clients) */}
      {viewingResults && (
        <WorkoutDetailsModal
          workout={viewingResults.workout}
          clientName={user.full_name}
          date={viewingResults.workout?.date}
          onClose={() => setViewingResults(null)}
          language="es"
        />
      )}
    </div>
  );
};

export default ClientDashboard;
